import { useContext, useEffect } from "preact/hooks";
import { GlobalContext } from "@/context/GlobalContext";
import CartItem from "@/components/CartItem";
import { formatTotal } from "@/Utils";
import CloseIcon from "@/icons/CloseIcon";
import EmptyCartGraphic from "@/icons/empty-cart-graphic.svg";
import useCart from "@/hooks/useCart";
import useSideCart from "@/hooks/useSideCart";
import useScrollBlock from '@/hooks/useScrollBlock';
import { surfsideTrackCartView } from "@/shared/surfside-analytics";

function SideCart() {
	const { checkout, setCheckout } = useContext(GlobalContext);
	const { trackCheckout, changeQuantityValue, removeFromCart, sideCartOpen, setSideCartOpen, removeLoading, quantityLoading } = useSideCart();
	const { scrollBlocked, setScrollBlocked, blockScroll, allowScroll } = useScrollBlock();
	useCart();

	//set focus on the first focusable element in the drawer
	if (sideCartOpen) {
		const drawer = document.querySelector(".sidecart");
		const focusableElements = drawer.querySelectorAll(
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
		);
		const firstFocusableElement = focusableElements[0];
		firstFocusableElement.focus();
	}

	function handleKeyDown(event) {
		if (event.key === "Escape" && sideCartOpen) {
			setSideCartOpen(false); //esc key closes the drawer
		}
	}
	document.addEventListener("keydown", handleKeyDown);

	useEffect(() => {
		if ( sideCartOpen ) {
			blockScroll();
			surfsideTrackCartView( checkout.items, checkout.pricingType );
		} else {
			allowScroll();
		}
	}, [sideCartOpen]);
	
	return (
		<div className={`drawer drawer-right sidecart ${sideCartOpen ? "is-open" : ""}`}>
			<div className="drawer-content">
				<header className="drawer-header sidecart-header">
					<h2>
						<span>Your Cart ({checkout.items?.length ? checkout.items.length : 0 })</span>
						<button
							onClick={() => setSideCartOpen(false)}
							className="btn-reset"
							aria-label="Close side cart"
						>
							<CloseIcon />
						</button>
					</h2>
				</header>
				{removeLoading || quantityLoading ? (
					<div className="sidecart-loading-overlay"></div>
				) : null}

				<section className="drawer-list sidecart-products">
					{checkout.items && checkout.items.length > 0 ? (
						checkout.items.map((cartItem, index) => (
							<CartItem
								key={cartItem.id}
								cartItem={cartItem}
								onChangeQuantity={changeQuantityValue}
								onRemoveFromCart={removeFromCart}
								index={index}
							/>
						))
					) : (
						<div className="sidecart-empty">
							<div className="sidecart-empty-graphic">
								<EmptyCartGraphic />
							</div>
							<p>Your cart is empty</p>
							<a className="btn-primary-outline" href="/shop">
								Start Shopping
							</a>
						</div>
					)}
				</section>

				{checkout.priceSummary && checkout.priceSummary.total > 0 ? (
					<footer className="drawer-footer sidecart-totals">
						<div className="sidecart-total-line">
							<label>Taxes:</label>{" "}
							<span>${formatTotal(checkout.priceSummary.taxes)}</span>
						</div>
						<div className="sidecart-total-line">
							<label>Subtotal:</label>{" "}
							<span>${formatTotal(checkout.priceSummary.subtotal)}</span>
						</div>

						{checkout.priceSummary.fees ? (
							<div className="sidecart-total-line">
								<label>Fees:</label>
								<span> ${formatTotal(checkout.priceSummary.fees)} </span>
							</div>
						) : null}

						{checkout.priceSummary.discounts ? (
							<div className="sidecart-total-line">
								<label>Discounts:</label>
								<span> -${formatTotal(checkout.priceSummary.discounts)} </span>
							</div>
						) : null}

						{checkout.priceSummary.rewards ? (
							<div className="sidecart-total-line">
								<label>Rewards:</label>
								<span> -${formatTotal(checkout.priceSummary.rewards)} </span>
							</div>
						) : null}

						<div className="sidecart-total-line sidecart-total">
							<label>Order Total:</label>
							<span> ${formatTotal(checkout.priceSummary.total)} </span>
						</div>

						<div className="sidecart-message">{dutchieSidecartMessage}</div>

						{checkout.redirectUrl ? (
							<a
								href={`${checkout.redirectUrl}?r=${dutchie.order_confirmed_page_url}`}
								className="btn-white drawer-button sidecart-checkout"
								onClick={trackCheckout}
							>
								Checkout
							</a>
						) : (
							<p className="badge error">No Checkout URL available.</p>
						)}
					</footer>
				) : null}
			</div>
			<div className="drawer-backdrop btn-reset" onClick={() => setSideCartOpen(false)}></div>
		</div>
	);
}
export default SideCart;
